import { create } from 'zustand';
import { message } from 'antd';
import apiCore from '../utils/api-core';
import {
    getCompanyContactsV3,
    createContactV3,
    deleteContactV3,
    exportContactsV3
} from '../reducers/contact/resources';
import session from '../utils/session';
import config from '../config';
import _ from 'lodash';
import { syncCompanyRoom } from '../io';

// `companyTags` backs the tag filter dropdown on the Contacts page and was only
// ever loaded once, by fetchFiltersData. Socket-driven tag changes left it
// stale, so removed tags lingered in the filter and new ones never showed up.
//
// Debounced because bulk tagging emits one socket event per contact, and the
// user who made the change also receives an echo of their own event. Lodash
// keeps the last args, so the most recent thunk wins.
const scheduleCompanyTagRefresh = _.debounce((refresh) => refresh(), 1000);

const useContactsStore = create((set, get) => ({
    // Data
    contacts: [],
    loading: false,
    pagination: {
        page: 1,
        pageSize: 20,
        total: 0,
        totalPages: 0
    },
    sort: {
        field: 'name',
        order: 'asc'
    },
    
    // Filters
    filters: {
        search: '',
        tags: [],
        groups: [],
        archived: false,
        departments: [],
        distance: 0
    },

    // Filters Data
    companyTags: [],
    availableGroups: [],
    availableDepartments: [],

    // Modals
    isContactModalOpen: false,
    isExportModalOpen: false,
    contactModalLoading: false,
    exportLoading: false,

    // The active company ID — for gh-admin users this is set by the company
    // selector; for regular users it falls back to their own company.
    activeCompanyId: null,
    setActiveCompanyId: (id) => set({ activeCompanyId: id }),

    // Actions
    fetchFiltersData: async () => {
        const companyId = get().activeCompanyId || session.get('userData')?.companyId;
        if (!companyId) return;

        // The Contacts page binds to no single contact, so the company room is
        // the only way its filter options hear about tag changes made
        // elsewhere. Joining here also covers a company switch, since this
        // re-runs when activeCompanyId changes.
        syncCompanyRoom(companyId);

        try {
            const [tagsRes, groupsRes, deptsRes] = await Promise.all([
                apiCore.get(`/api/v3/company/${companyId}/tags`),
                apiCore.get(`/api/v3/groups/company/${companyId}`),
                apiCore.get(`/api/v3/company/${companyId}/departments`)
            ]);

            const tags = Array.isArray(tagsRes.data) ? tagsRes.data : (Array.isArray(tagsRes.data?.data) ? tagsRes.data.data : []);
            const groups = groupsRes.data.data || groupsRes.data || [];
            const depts = deptsRes.data.data || deptsRes.data || [];

            set({
                companyTags: tags,
                availableGroups: groups,
                availableDepartments: depts
            });
        } catch (error) {
            console.error('Failed to load filter options:', error);
            message.error('Failed to load filter options');
        }
    },

    // Actions
    setFilters: (newFilters) => {
        set((state) => ({
            filters: { ...state.filters, ...newFilters },
            pagination: { ...state.pagination, page: 1 } // Reset to page 1 on filter change
        }));
        get().fetchContacts();
    },

    setPage: (page, pageSize) => {
        set((state) => ({
            pagination: { ...state.pagination, page, pageSize }
        }));
        get().fetchContacts();
    },

    setSort: (field, order) => {
        set((state) => ({ 
            sort: { field, order },
            pagination: { ...state.pagination, page: 1 }
        }));
        get().fetchContacts();
    },

    setIsContactModalOpen: (open) => set({ isContactModalOpen: open }),
    setIsExportModalOpen: (open) => set({ isExportModalOpen: open }),

    fetchContacts: async () => {
        set({ loading: true });
        const { pagination, filters, sort } = get();
        const companyId = get().activeCompanyId || session.get('userData')?.companyId;

        if (!companyId) {
            set({ loading: false });
            return;
        }

        try {
            const params = {
                page: pagination.page,
                pageSize: pagination.pageSize,
                search: filters.search,
                tags: filters.tags,
                groups: filters.groups,
                departments: filters.departments,
                distance: filters.distance,
                archived: filters.archived,
                sort: sort.field,
                order: sort.order
            };

            const res = await getCompanyContactsV3(companyId, params);
            const data = res.data.data || res.data;

            set({
                contacts: data.rows || [],
                pagination: {
                    page: data.page || 1,
                    pageSize: data.pageSize || 20,
                    total: data.total || 0,
                    totalPages: data.totalPages || 0,
                    archivedCount: data.archivedCount || 0,
                    totalActiveContacts: data.totalActiveContacts || 0
                },
                loading: false
            });
        } catch (error) {
            console.error('Failed to fetch contacts:', error);
            set({ loading: false });
            message.error('Failed to load contacts');
        }
    },

    createContact: async (values) => {
        set({ contactModalLoading: true });
        const companyId = get().activeCompanyId || session.get('userData')?.companyId;
        
        try {
            const payload = {
                ...values,
                companyId
            };

            await createContactV3(payload);
            message.success('Contact created successfully');
            set({ isContactModalOpen: false, contactModalLoading: false });
            get().fetchContacts(); // Refresh list
            get().fetchFiltersData(); // Refresh filter options (tags, groups)
        } catch (error) {
            console.error('Failed to create contact:', error);
            set({ contactModalLoading: false });
            // Extract error message if available
            const errorMsg = error.response?.data?.message || 'Failed to create contact';
            message.error(errorMsg);
        }
    },

    deleteContact: async (contactId) => {
        try {
            await deleteContactV3(contactId);
            message.success('Contact deleted successfully');
            get().fetchContacts(); // Refresh list
        } catch (error) {
            console.error('Failed to delete contact:', error);
            message.error('Failed to delete contact');
        }
    },

    exportContacts: async () => {
        set({ exportLoading: true });
        const { filters } = get();
        try {
            const params = {
                search: filters.search
                // Add other export filters if API supports
            };
            const res = await exportContactsV3(params);
            const { fileName } = res.data;
            
            if (fileName) {
                message.success('Export started — downloading file');
                // Use a hidden anchor with download attribute to trigger a true
                // browser download without navigating away from the SPA.
                const link = document.createElement('a');
                link.href = `${config.api.host}/api/v1/files/contact/${fileName}`;
                link.download = fileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                message.warning('No file generated');
            }
            set({ isExportModalOpen: false, exportLoading: false });
        } catch (error) {
            console.error('Failed to export contacts:', error);
            set({ exportLoading: false });
            message.error('Failed to export contacts');
        }
    },

    applyContactTagsUpdated: ({ contactId, companyId, tags }) => {
        // Refresh the tag filter options first: this must happen even when the
        // contact isn't on the current page (the early return below), since a
        // tag change on any contact in the company can add or remove a filter
        // option.
        const cid = companyId || get().activeCompanyId || session.get('userData')?.companyId;
        if (cid) scheduleCompanyTagRefresh(() => get().refreshCompanyTags(cid));

        const { contacts } = get();
        const idx = contacts.findIndex(c => c.id === contactId);
        if (idx === -1) return;
        // ContactsV2 renders tags as a string[] of tag values.
        const next = [...contacts];
        next[idx] = { ...next[idx], tags: (tags || []).map(t => t.value) };
        set({ contacts: next });
    },

    // Narrow counterpart to fetchFiltersData: only re-reads the tag list, so a
    // tag change doesn't also refetch groups and departments.
    refreshCompanyTags: async (companyId) => {
        const cid = companyId || get().activeCompanyId || session.get('userData')?.companyId;
        if (!cid) return;
        try {
            const res = await apiCore.get(`/api/v3/company/${cid}/tags`);
            const tags = Array.isArray(res.data)
                ? res.data
                : (Array.isArray(res.data?.data) ? res.data.data : []);
            set({ companyTags: tags });
        } catch (error) {
            console.error('Failed to refresh company tags:', error);
        }
    },

    applyContactStarUpdated: ({ contactId, star }) => {
        const { contacts } = get();
        const idx = contacts.findIndex(c => c.id === contactId);
        if (idx === -1) return;
        const next = [...contacts];
        next[idx] = { ...next[idx], star: !!star };
        set({ contacts: next });
    },

    applyContactGroupsUpdated: ({ contactId, groups }) => {
        const { contacts } = get();
        const idx = contacts.findIndex(c => c.id === contactId);
        if (idx === -1) return;
        const userId = session.get('userData')?.id;
        const visible = (groups || []).filter(g =>
            g.privacy !== 'private' || (userId && g.user_id === userId)
        );
        // ContactsV2 renders groups as a { id, name }[].
        const next = [...contacts];
        next[idx] = { ...next[idx], groups: visible.map(g => ({ id: g.id, name: g.name })) };
        set({ contacts: next });
    }
}));

export default useContactsStore;
